import { APP_TIMEZONE } from '@/constants/timezone';
import { BMT_ENROLMENT_STATUS } from '@/constants/bmt';

/**
 * Display helpers for BMT Companion. Pure functions of the API's views, so the
 * screens hold no date or status logic of their own and every branch is testable.
 *
 * Times arrive as UTC instants and are always shown in Singapore time, whatever
 * zone the phone is in: a trainee abroad still has to be in the hall at 7 pm SGT.
 * `date` fields are already Singapore calendar dates (YYYY-MM-DD).
 */

const partsOf = (date, options) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', options)
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value]),
  );

const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

const parseCalendarDate = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  // Noon UTC, formatted in UTC: the calendar date cannot drift a day either way.
  const date = new Date(`${value}T12:00:00Z`);
  return isValidDate(date) ? date : null;
};

/** '2026-11-07' → 'Sat, 7 Nov'. */
export function formatDayDate(value) {
  const date = parseCalendarDate(value);
  if (!date) return '';
  const { weekday, day, month } = partsOf(date, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${weekday}, ${day} ${month}`;
}

/** '2026-11-07' → '7 Nov'. */
export function formatShortDate(value) {
  const date = parseCalendarDate(value);
  if (!date) return '';
  const { day, month } = partsOf(date, { timeZone: 'UTC', day: 'numeric', month: 'short' });
  return `${day} ${month}`;
}

/** A UTC instant → '6:00 pm' in Singapore time. */
export function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (!isValidDate(date)) return '';
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(date)
      // Some ICU builds put a narrow no-break space before am/pm; \s matches it.
      .replace(/\s/g, ' ')
      .toLowerCase()
  );
}

/** '7:00 pm – 9:30 pm', or just the start when there is no end. */
export function formatTimeRange(startsAt, endsAt) {
  const start = formatTime(startsAt);
  const end = formatTime(endsAt);
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

/** The first and last date of a run of days: 'Sat, 7 Nov – Sat, 28 Nov'. */
export function formatDateSpan(days = []) {
  const dates = days.map(day => day.date).filter(Boolean);
  if (!dates.length) return '';
  const first = formatDayDate(dates[0]);
  const last = formatDayDate(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

/**
 * The check-in line for a day. The app never offers self check-in: the only
 * way in is scanning the QR shown in the hall, so `canScan` is just a hint for
 * which button to emphasise.
 */
export function checkinLabel(checkin) {
  const state = checkin?.state || 'none';
  if (state === 'not-yet') {
    const at = formatTime(checkin.opensAt);
    return {
      state,
      text: at ? `Check-in opens at ${at}` : 'Check-in not open yet',
      canScan: false,
    };
  }
  if (state === 'open') return { state, text: 'Check-in open', canScan: true };
  if (state === 'closed') return { state, text: 'Check-in closed', canScan: false };
  return { state: 'none', text: '', canScan: false };
}

/** A session's status line: 'Upcoming', 'Attended', 'Made up on 14 Nov', 'Missed'. */
export function sessionStatusLabel(session) {
  if (session?.status === 'attended') {
    return session.attendedOn ? `Made up on ${formatShortDate(session.attendedOn)}` : 'Attended';
  }
  if (session?.status === 'missed') return 'Missed';
  return 'Upcoming';
}

/**
 * One status for a whole day: upcoming while any session is still to come,
 * attended when every session is, partial when some were missed, missed when
 * none were attended.
 */
export function dayStatus(day) {
  const sessions = day?.sessions || [];
  if (!sessions.length || sessions.some(s => s.status === 'upcoming')) return 'upcoming';
  const attended = sessions.filter(s => s.status === 'attended').length;
  if (attended === sessions.length) return 'attended';
  return attended ? 'partial' : 'missed';
}

const DAY_STATUS_TEXT = {
  upcoming: 'Upcoming',
  attended: 'Attended',
  partial: 'Partly attended',
  missed: 'Missed',
};

export const dayStatusText = day => DAY_STATUS_TEXT[dayStatus(day)];

/**
 * The day a trainee should look at next: today's while check-in is not over,
 * otherwise the first one still to come. Null once every day has passed.
 */
export function nextDay(days = [], serverTime) {
  const now = serverTime ? new Date(serverTime) : new Date();
  return (
    days.find(day => {
      if (day.checkin?.state === 'open' || day.checkin?.state === 'not-yet') return true;
      if (day.checkin?.state === 'closed') return false;
      const end = day.endsAt ? new Date(day.endsAt) : null;
      if (end && isValidDate(end)) return end > now;
      return dayStatus(day) === 'upcoming';
    }) || null
  );
}

/** The Singapore calendar date of a UTC instant (or now), as YYYY-MM-DD. */
export function sgCalendarDate(value) {
  const date = value ? new Date(value) : new Date();
  if (!isValidDate(date)) return '';
  const { year, month, day } = partsOf(date, {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${year}-${month}-${day}`;
}

/** A day's topic titles, joined: 'Prayer · Serving in Church'. */
export const topicsText = day => (day?.sessions || []).map(s => s.title).join(' · ');

/** Time range and venue on one line: '7:30 pm – 10:00 pm · Hall 2'. */
export const dayTimeVenue = day =>
  [formatTimeRange(day?.startsAt, day?.endsAt), day?.venue].filter(Boolean).join(' · ');

/** Today's day (Singapore date, by the server's clock) while its check-in is open, or null. */
export function openCheckinDay(me) {
  if (!['pending', 'approved'].includes(bmtPhase(me))) return null;
  const today = sgCalendarDate(me.serverTime);
  return (me.days || []).find(day => day.date === today && day.checkin?.state === 'open') || null;
}

/**
 * The "J1 Today first" journey: topic progress, the hero day (today's, else the
 * next one to come), the rest still to come, and the days already behind.
 * Days are split by Singapore calendar date against the server's clock.
 */
export function journeyModel(me) {
  const days = me?.days || [];
  const today = sgCalendarDate(me?.serverTime);
  const sessions = days.flatMap(day => day.sessions || []);
  const todays = days.find(day => day.date === today) || null;
  const future = days.filter(day => day.date > today);
  const hero = todays
    ? { day: todays, label: 'Today' }
    : future.length
      ? { day: future[0], label: 'Next' }
      : null;
  return {
    total: sessions.length,
    done: sessions.filter(s => s.status === 'attended').length,
    hero,
    comingUp: future.filter(day => day !== hero?.day),
    past: days.filter(day => day.date < today),
  };
}

/** A past day's one-word summary in the Done list. */
export function pastDaySummary(day) {
  const status = dayStatus(day);
  if (status === 'attended') return '✓ Attended';
  if (status === 'missed') return 'Missed';
  return dayStatusText(day);
}

/** Minutes from `now` until an instant, rounded up; null when unknown or past. */
function minutesUntil(value, now) {
  const at = value ? Date.parse(value) : NaN;
  if (Number.isNaN(at)) return null;
  const minutes = Math.ceil((at - now) / 60000);
  return minutes > 0 ? minutes : null;
}

/** Under this many minutes away, the not-open screen also says how long to wait. */
const NOT_OPEN_COUNTDOWN_MINUTES = 90;

/**
 * What the /bmt/check-in screen says for a scan response kept in the store
 * (`bmt.lastCheckIn`: `{ status, code, display, checkIn, opensAt }`). `checkedInAt`
 * is optional; without it the time is simply left out.
 */
export function checkInResultModel(result, now = Date.now()) {
  const checkIn = result?.checkIn || {};
  const time = formatTime(checkIn.checkedInAt);
  if (result?.status === 'success') {
    if (result.code === 'ALREADY_CHECKED_IN' || checkIn.alreadyCheckedIn === true) {
      return {
        kind: 'already',
        title: 'Already checked in',
        message: time ? `You checked in at ${time}. Nothing else to do.` : 'Nothing else to do.',
      };
    }
    return {
      kind: 'checked-in',
      title: "You're checked in",
      subtitle: [formatDayDate(checkIn.date), time].filter(Boolean).join(' · '),
      date: checkIn.date || null,
      sessions: checkIn.sessions || [],
      pending: checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.PENDING,
      approved: checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.APPROVED,
    };
  }
  if (result?.code === 'CHECKIN_NOT_OPEN') {
    const opens = formatTime(result.opensAt);
    const minutes = minutesUntil(result.opensAt, now);
    const countdown =
      minutes && minutes < NOT_OPEN_COUNTDOWN_MINUTES
        ? `That's in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}. `
        : '';
    return {
      kind: 'not-open',
      title: opens ? `Check-in opens at ${opens}` : "Check-in isn't open yet",
      message: `${countdown}Scan the same code again then.`,
    };
  }
  if (result?.code === 'CHECKIN_CLOSED') {
    return {
      kind: 'closed',
      title: 'Check-in has closed',
      message: 'If you were here, ask the BMT team to mark you present.',
    };
  }
  return {
    kind: 'error',
    title: "That didn't work",
    message: result?.display?.message || '',
    help: 'Ask the BMT team for help.',
  };
}

const isEnrolled = me =>
  Boolean(me?.enrolment) && me.enrolment.status !== BMT_ENROLMENT_STATUS.WITHDRAWN;

/**
 * Which face the BMT hub shows: 'signup' (not enrolled, or withdrawn, with an
 * intake open), 'pending', 'approved', or 'none' (nothing to offer).
 */
export function bmtPhase(me) {
  if (!me) return 'none';
  if (isEnrolled(me)) {
    return me.enrolment.status === BMT_ENROLMENT_STATUS.APPROVED ? 'approved' : 'pending';
  }
  return me.openIntakes?.length ? 'signup' : 'none';
}

/** The earliest first date across the open intakes, for "BMT starts …". */
export function earliestStart(openIntakes = []) {
  const firsts = openIntakes.map(intake => intake.days?.[0]?.date).filter(Boolean);
  return firsts.length ? firsts.sort()[0] : null;
}

/**
 * Everything the home card says, or null when it should not render.
 * Trainee copy says "BMT" and never an intake or batch number.
 */
export function homeCardModel(me) {
  const phase = bmtPhase(me);
  if (phase === 'signup') {
    const start = earliestStart(me.openIntakes);
    return {
      phase,
      title: start ? `BMT starts ${formatDayDate(start)}` : 'BMT sign-up is open',
      detail: 'Sign up',
    };
  }
  if (phase === 'pending') {
    return { phase, title: 'BMT', detail: 'Waiting for the BMT team to confirm' };
  }
  if (phase === 'approved') {
    const day = nextDay(me.days, me.serverTime);
    if (!day) return { phase, title: 'BMT', detail: 'All your BMT days are done' };
    const time = formatTimeRange(day.startsAt, day.endsAt);
    return {
      phase,
      title: `BMT · ${formatDayDate(day.date)}`,
      detail: [time, day.venue].filter(Boolean).join(' · '),
      checkin: checkinLabel(day.checkin),
    };
  }
  return null;
}

const ERROR_MESSAGES = {
  CG_NOTE_REQUIRED: 'Let us know whether you are in a Connect Group first.',
  INTAKE_NOT_OPEN: 'Sign-up for that BMT has just closed. The list now shows what is open.',
  ALREADY_ENROLLED: 'You are already signed up for BMT.',
  NOT_ENROLLED: 'Sign up for BMT to see your homework.',
  NOT_APPROVED: 'Homework opens once the BMT team confirm your place.',
  HOMEWORK_NOT_FOUND: 'That homework is no longer available.',
  TEXT_REQUIRED: 'Write your answer before submitting.',
};

/** User-facing copy for an API error body `{ code, message }`, or a network failure. */
export function bmtErrorMessage(error) {
  const code = error?.code || error?.response?.data?.code;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return 'Something went wrong. Please try again.';
}
